// Hosts directory (spec §6.4). There is no dedicated "list every business"
// admin function — the approved-business list ops-pending already serves
// the mobile Approvals screen (`opsDB.listApproved`) doubles as the live
// host directory here, since it's the same "businesses with review_status =
// approved" set. A business id can also be pasted straight in (e.g. from a
// booking's business link on /bookings/$id) to jump to a host that search
// hasn't loaded yet.
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { opsDB } from '@/lib/data/ops';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/hosts/')({
  component: HostsList,
});

function HostsList() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [jumpId, setJumpId] = useState('');

  const approvedQuery = useQuery({
    queryKey: ['ops-pending', 'approved'],
    queryFn: async () => {
      const { data, error } = await opsDB.listApproved(supabase);
      if (error) throw error;
      return data ?? [];
    },
  });

  const filtered = useMemo(() => {
    const rows = approvedQuery.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => [b.name, b.category, b.location, b.ownerName].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [approvedQuery.data, query]);

  return (
    <section className="flex flex-col gap-lg">
      <div>
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Hosts</h1>
        <p className="mt-sm text-body text-ink-2">
          Strikes, pay-rail history, full-prepay unlock, team sessions — click a host, or jump to a business id.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (jumpId.trim()) void navigate({ to: '/hosts/$id', params: { id: jumpId.trim() } });
        }}
        className="flex flex-wrap items-center gap-sm"
      >
        <input
          type="text"
          value={jumpId}
          onChange={(e) => setJumpId(e.target.value)}
          placeholder="Business id…"
          className="rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
        />
        <button type="submit" className="rounded-button border border-border px-md py-sm text-body text-ink">
          Open
        </button>
      </form>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search live hosts by name, category, location or owner"
        className="rounded-button border border-border bg-surface px-md py-sm text-body text-ink"
      />

      {approvedQuery.isLoading ? <PageLoading /> : null}
      {approvedQuery.isError ? <p className="text-body text-danger">Couldn't load hosts.</p> : null}

      {filtered.length > 0 ? (
        <div className="flex flex-col gap-sm">
          {filtered.map((b) => (
            <Link
              key={b.id}
              to="/hosts/$id"
              params={{ id: b.id }}
              className="rounded-card border border-border bg-surface p-lg shadow-card hover:bg-surface-2"
            >
              <h3 className="text-h3 font-bold text-ink">{b.name ?? 'Unnamed business'}</h3>
              <p className="mt-xs text-caption text-ink-2">
                {(b.category ?? 'uncategorised')}
                {b.location ? ` · ${b.location}` : ''}
                {b.ownerName ? ` · ${b.ownerName}` : ''}
              </p>
            </Link>
          ))}
        </div>
      ) : approvedQuery.isSuccess ? (
        <p className="rounded-card border border-border bg-surface p-xl text-center text-body text-ink-2">No hosts match.</p>
      ) : null}
    </section>
  );
}
